import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import api, { API, apiError } from "@/lib/api";

// Downloads the finished MP4 for a published/rendered video. It never renders —
// rendering happens via "Publish" on the video page. Disabled until render_ready.
// Uses a native browser download (direct URL + media token) so large files download
// reliably via the browser's own download manager, and can be re-triggered anytime
// if a download doesn't finish.
export const DownloadVideoButton = ({ videoId, renderReady = false, className = "" }) => {
  const [preparing, setPreparing] = useState(false);

  const download = async (e) => {
    // The card is a link — never navigate when the button is clicked.
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!renderReady) {
      toast.error("Publish this video first, then download.");
      return;
    }
    setPreparing(true);
    try {
      const { data: mt } = await api.get(`/videos/${videoId}/media-token`);
      const url = `${API}/videos/${videoId}/download?token=${encodeURIComponent(mt.token)}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `videoileo-${videoId}.mp4`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Download started — you can click Download again anytime if it doesn't finish.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPreparing(false);
    }
  };

  return (
    <button
      onClick={download}
      disabled={preparing || !renderReady}
      data-testid={`library-download-btn-${videoId}`}
      title={renderReady ? "Download the finished MP4" : "Publish this video first, then download"}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${renderReady ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-white/5 text-zinc-500"} ${className}`}
    >
      {preparing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      {preparing ? "Preparing…" : "Download"}
    </button>
  );
};

export default DownloadVideoButton;
